import { AlertTriangle } from 'lucide-react';

import type { ModalBaseProps } from '@types';

interface InactivityWarningModalProps extends ModalBaseProps {
  timeRemaining: number;
  onStayActive: () => void;
  onLogout: () => void;
}

export const InactivityWarningModal = ({
  isOpen,
  timeRemaining,
  onStayActive,
  onLogout,
}: InactivityWarningModalProps) => {
  if (!isOpen) return null;

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-night/50 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in duration-200 font-sans">
        {/* Warning Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">Are you still there?</h2>

        {/* Message */}
        <p className="text-gray-600 text-center mb-6">
          You&apos;ve been inactive for a while. For your security, you&apos;ll be automatically
          logged out in:
        </p>

        {/* Countdown Timer */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg p-4 mb-6">
          <div className="text-center">
            <div className="text-4xl font-bold text-amber-600 font-sans">
              {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
            </div>
            <div className="text-sm text-amber-700 mt-1">minutes remaining</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onLogout}
            className="flex-1 px-4 py-3 text-sm font-medium font-sans text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Log Out Now
          </button>
          <button
            type="button"
            onClick={onStayActive}
            className="btn-primary flex-1 h-auto px-4 py-3 text-sm shadow-sm"
          >
            Stay Active
          </button>
        </div>

        {/* Footer Note */}
        <p className="text-xs text-gray-500 text-center mt-4">
          This helps protect your account when you&apos;re away from your device.
        </p>
      </div>
    </div>
  );
};
